import React from 'react';
import * as Yup from 'yup';

import {
  ApplicationFlow,
  TextInput,
  SelectInput,
  SSNField,
} from '../components';
import { checkSSN } from '../utils';
import { documentTypes } from '../data';


const Description = () => (
  <React.Fragment>
    <p>
      An estate (or decedent estate) or succession is a legal entity created as
      a result of a person's death. The estate consists of the real estate and/or personal property
      of the deceased person. The estate pays any debts owed by the decedent, and distributes the
      balance of the estate's assets to the beneficiaries of the estate
    </p>
  </React.Fragment>
);


const fieldSets = [
  {
    legend: 'Deceased Individual Information',
    fields: new Map([
      ['deceasedFirstName', {
        component: TextInput,
        label: 'First Name',
        title: 'Please provide a valid name',
        'data-help-title': 'Deceased Information',
        'data-help-text': 'Enter information of the Deceased individual.',
        validation: Yup.string().required('Please provide a valid name'),
      }],
      ['deceasedMiddleName', {
        component: TextInput,
        label: 'Middle Name',
        placeholder: 'Optional',
        'data-help-title': 'Deceased Information',
        'data-help-text': 'Enter information of the Deceased individual.',
        validation: Yup.string(),
      }],
      ['deceasedLastName', {
        component: TextInput,
        label: 'Last Name',
        title: 'Please provide a valid name',
        'data-help-title': 'Deceased Information',
        'data-help-text': 'Enter information of the Deceased individual.',
        validation: Yup.string().required('Please provide a valid name'),
      }],
      ['deceasedSocialSecurity', {
        component: SSNField,
        label: 'Social Security Number',
        placeholder: '123-45-6789',
        title: 'Please provide a valid Social Security Number Ex: 123-45-6789',
        'data-help-title': 'Deceased Information',
        'data-help-text': 'Enter information of the Deceased individual.',
        validation: (Yup
          .string()
          .min(11, 'Please provide a valid Social Security Number Ex: 123-45-6789')
          .test(
            'ssn-is-valid',
            'Please provide a valid Social Security Number Ex: 123-45-6789',
            checkSSN,
          )
          .required('Please provide a valid Social Security Number Ex: 123-45-6789')
        ),
      }],
      ['deceasedVerifySocialSecurity', {
        component: SSNField,
        label: 'Verify SSN',
        placeholder: '123-45-6789',
        title: 'Please verify the Social Security Number',
        'data-help-title': 'Deceased Information',
        'data-help-text': 'Enter information of the Deceased individual.',
        validation: (Yup
          .string()
          .oneOf(
            [Yup.ref('deceasedSocialSecurity'), null],
            'Please verify the Social Security Number',
          )
          .required('Please verify the Social Security Number')
        ),
      }],
      ['suffix', {
        initial: 'DDS',
      }],
    ]),
  },
  {
    legend: 'Executor/Personal Representative Information',
    fields: new Map([
      ['executorFirstName', {
        component: TextInput,
        label: 'First Name',
        title: 'Please provide a valid name',
        'data-help-title': 'Executor Information',
        'data-help-text': "This is the Responsible Party for the estate. The Responsible Party is the person who has a level of control over or entitlement to the funds or assets of an estate. It may also be referred to as the estate's Executor, Administrator, or Personal Representative.",
        validation: Yup.string().required('Please provide a valid name'),
      }],
      ['executorMiddleName', {
        component: TextInput,
        label: 'Middle Name',
        placeholder: 'Optional',
        'data-help-title': 'Executor Information',
        'data-help-text': "This is the Responsible Party for the estate. The Responsible Party is the person who has a level of control over or entitlement to the funds or assets of an estate. It may also be referred to as the estate's Executor, Administrator, or Personal Representative.",
        validation: Yup.string(),
      }],
      ['executorLastName', {
        component: TextInput,
        label: 'Last Name',
        title: 'Please provide a valid name',
        'data-help-title': 'Executor Information',
        'data-help-text': "This is the Responsible Party for the estate. The Responsible Party is the person who has a level of control over or entitlement to the funds or assets of an estate. It may also be referred to as the estate's Executor, Administrator, or Personal Representative.",
        validation: Yup.string().required('Please provide a valid name'),
      }],
      ['executorSocialSecurity', {
        component: SSNField,
        label: 'Social Security Number',
        placeholder: '123-45-6789',
        title: 'Please provide a valid Social Security Number Ex: 123-45-6789',
        'data-help-title': 'Executor Information',
        'data-help-text': "This is the Responsible Party for the estate. The Responsible Party is the person who has a level of control over or entitlement to the funds or assets of an estate. It may also be referred to as the estate's Executor, Administrator, or Personal Representative.",
        validation: (Yup
          .string()
          .min(11, 'Please provide a valid Social Security Number Ex: 123-45-6789')
          .test(
            'ssn-is-valid',
            'Please provide a valid Social Security Number Ex: 123-45-6789',
            checkSSN,
          )
          .required('Please provide a valid Social Security Number Ex: 123-45-6789')
        ),
      }],
      ['executorVerifySocialSecurity', {
        component: SSNField,
        label: 'Verify SSN',
        placeholder: '123-45-6789',
        title: 'Please verify the Social Security Number',
        'data-help-title': 'Executor Information',
        'data-help-text': "This is the Responsible Party for the estate. The Responsible Party is the person who has a level of control over or entitlement to the funds or assets of an estate. It may also be referred to as the estate's Executor, Administrator, or Personal Representative.",
        validation: (Yup
          .string()
          .oneOf(
            [Yup.ref('executorSocialSecurity'), null],
            'Please verify the Social Security Number',
          )
          .required('Please verify the Social Security Number')
        ),
      }],
      ['executorTitle', {
        initial: 'executor',
        component: SelectInput,
        options: [
          { value: 'executor', label: 'Executor' },
          { value: 'administrator', label: 'Administrator' },
          { value: 'personal_representative', label: 'Personal Representative' },
        ],
        label: 'Title',
        title: 'Executor Information',
        'data-help-title': 'Owner Title',
        'data-help-text': "This is the Responsible Party for the estate. The Responsible Party is the person who has a level of control over or entitlement to the funds or assets of an estate. It may also be referred to as the estate's Executor, Administrator, or Personal Representative.",
        validation: Yup.string().required('Please select the valid title'),
      }],
      ['suffix', {
        initial: 'DDS',
      }],
    ]),
  },
  {
    legend: 'Executor/Personal Representative address (No PO Boxes)',
    fields: new Map([
      ['businessAddress'],
      ['businessCity'],
      ['businessState'],
      ['businessZip'],
      ['businessCounty'],
      ['mailingAddressDiff'],
    ]),
  },
  {
    legend: 'Estate Dates',
    fields: new Map([
      ['businessDateStart', {
        label: 'Date of death',
      }],
      ['businessClosingMonth'],
    ]),
  },
  {
    legend: 'Contact Information',
    fields: new Map([
      ['customerContactPhone'],
      ['customerContactEmail'],
      ['customerContactEmailVerify'],
    ]),
  },
];


const EstateApplicationPage = props => (
  <ApplicationFlow
    step={2}
    title="Estate Application"
    documentType={documentTypes.ESTATE_DECEASED_INDIVIDUAL}
    description={Description}
    fieldSets={fieldSets}
    {...props}
  />
);

export default EstateApplicationPage;



// WEBPACK FOOTER //
// ./src/pages/estate-application.js